import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { MosaicComoCreateAccountCardBlockConfig } from '@front-platform/content-builder';
import { mosaicComoCreateAccountCardBlockResolveConfig } from '@front-platform/content-builder';
import { of } from 'rxjs';
import { Component, OnInit, input } from '@angular/core';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';

import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { MarketplaceStoreRefiner } from '@common/refiners/marketplace.refiner';

import { ComoUser } from '@core/models/como.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';

import { ComoService } from '@core/services/como/como.service';
import { ModalsService } from 'src/app/core/services/modals/modals.service';
import { AuthFirebaseService } from 'src/app/core/services/api/auth-firebase/auth-firebase.service';

import { AuthenticationModalComponent } from '@shared/modals/authentication-modal/authentication-modal.component';
import { ComoCreateAccountModalComponent } from '@shared/modals/como/como-create-account-modal/como-create-account-modal.component';
export interface MosaicComoCreateAccountCardBlockRuntimeConfig {}

export type MosaicComoCreateAccountCardBlockConfigInput = Partial<
  Omit<
    MosaicComoCreateAccountCardBlockConfig,
    keyof MosaicComoCreateAccountCardBlockRuntimeConfig
  > &
    MosaicComoCreateAccountCardBlockRuntimeConfig
>;
export type MosaicComoCreateAccountCardBlockResolvedConfig = Omit<
  MosaicComoCreateAccountCardBlockConfig,
  keyof MosaicComoCreateAccountCardBlockRuntimeConfig
> &
  Partial<MosaicComoCreateAccountCardBlockRuntimeConfig>;
export interface MosaicComoCreateAccountCardBlockContract extends ConfigComponentContract<
  MosaicComoCreateAccountCardBlockConfigInput,
  MosaicComoCreateAccountCardBlockResolvedConfig
> {}

@Component({
  selector: 'app-mosaic-como-create-account-card-block',
  templateUrl: './mosaic-como-create-account-card-block.component.html',
  styleUrls: ['./mosaic-como-create-account-card-block.component.scss'],
  standalone: false,
})
export class MosaicComoCreateAccountCardBlockComponent
  extends DestroyerBase
  implements OnInit, MosaicComoCreateAccountCardBlockContract
{
  readonly config = input<MosaicComoCreateAccountCardBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    MosaicComoCreateAccountCardBlockConfig,
    MosaicComoCreateAccountCardBlockRuntimeConfig
  >(mosaicComoCreateAccountCardBlockResolveConfig, () => ({ config: this.config() }));
  get heading(): string | undefined {
    return this.resolvedConfig()['heading'] as unknown as string | undefined;
  }
  get text(): string | undefined {
    return this.resolvedConfig()['text'] as unknown as string | undefined;
  }
  get backgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get textColor(): IContentBuilderFieldColor | undefined | undefined {
    return this.resolvedConfig()['text_color'] as unknown as
      IContentBuilderFieldColor | undefined | undefined;
  }

  isComoEnabled$ = this.marketplaceRefiner.selectIsComoPluginEnabled;
  public comoUser?: ComoUser | null;
  isLoading = true;

  constructor(
    private readonly comoService: ComoService,
    private cartDispatcher: CartStoreDispatcher,
    private readonly modalsService: ModalsService,
    readonly authFirebaseService: AuthFirebaseService,
    private marketplaceRefiner: MarketplaceStoreRefiner,
  ) {
    super();
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.isComoEnabled$
      .pipe(
        takeUntil(this._destroyerRef),
        switchMap(isComoEnabled => {
          if (!isComoEnabled) {
            this.isLoading = false;
            return of(false);
          }
          this.comoService.loadComoUser();
          return this.comoService.getComoUser$().pipe(
            takeUntil(this._destroyerRef),
            tap(comoUser => (this.comoUser = comoUser)),
            tap(() => (this.isLoading = false)),
          );
        }),
      )
      .subscribe();
  }

  onClick(): void {
    this.authFirebaseService
      .isAuthenticate$()
      .pipe(take(1))
      .subscribe(isAuthenticated => {
        if (!isAuthenticated || this.authFirebaseService.isAnonymous) {
          this.cartDispatcher.resetActive();
          this.modalsService.open(AuthenticationModalComponent.handle);
        } else {
          this.modalsService.open(ComoCreateAccountModalComponent.handle);
        }
      });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(MosaicComoCreateAccountCardBlockComponent, {
  'keyAliases': {},
});
